import { useState } from "react";
import {
  Building2,
  Cpu,
  Factory,
  GraduationCap,
  HeartPulse,
  Landmark,
  BarChart2,
  Grid,
} from "lucide-react";
import type { RansomwareSector } from "../../../api/status";
import { formatNumber, formatPercent, calculateShare } from "../../../utils/ransomwareUtils";

interface SectorsPanelProps {
  sectors: RansomwareSector[];
  totalAttacks: number;
  selectedSector: string | null;
  onSelectSector: (sectorName: string | null) => void;
}

export default function SectorsPanel({
  sectors,
  totalAttacks,
  selectedSector,
  onSelectSector,
}: SectorsPanelProps) {
  const [viewMode, setViewMode] = useState<"bars" | "heatmap">("bars");

  const safeSectors = Array.isArray(sectors) ? sectors : [];
  const counts = safeSectors.map((s) => (typeof s?.count === "number" ? s.count : 0));
  const rawMax = counts.length > 0 ? Math.max(...counts) : 1;
  const maxCount = isFinite(rawMax) && rawMax > 0 ? rawMax : 1;

  // Helper to map sector name to appropriate Lucide Icon
  const getSectorIcon = (secName: string) => {
    const s = secName.toLowerCase();
    if (s.includes("tech") || s.includes("software")) return <Cpu size={15} />;
    if (s.includes("health") || s.includes("medical")) return <HeartPulse size={15} />;
    if (s.includes("manufactur") || s.includes("industrial")) return <Factory size={15} />;
    if (s.includes("financ") || s.includes("bank")) return <Landmark size={15} />;
    if (s.includes("educat") || s.includes("school")) return <GraduationCap size={15} />;
    return <Building2 size={15} />;
  };

  return (
    <div
      className="rw-sectors-panel"
      style={{
        background: "var(--dash-card-bg, rgba(15, 23, 42, 0.6))",
        border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
        borderRadius: 12,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        minHeight: "440px", // Fixed minimum height matching leaderboard
        backdropFilter: "blur(12px)",
        boxSizing: "border-box",
      }}
    >
      {/* HEADER & TOGGLE */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h3
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "var(--dash-text-primary, #f8fafc)",
              margin: 0,
            }}
          >
            Most Impacted Sectors
          </h3>
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--dash-text-muted, #94a3b8)",
              marginTop: 2,
              margin: 0,
            }}
          >
            Industry category distribution and longitudinal exposure
          </p>
        </div>

        {/* VIEW MODE TOGGLE */}
        <div
          style={{
            display: "inline-flex",
            padding: 2,
            background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.04))",
            borderRadius: 6,
            border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.1))",
          }}
        >
          <button
            onClick={() => setViewMode("bars")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: "0.75rem",
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 4,
              background: viewMode === "bars" ? "var(--dash-indigo, #6366F1)" : "transparent",
              color: viewMode === "bars" ? "#ffffff" : "var(--dash-text-muted, #94a3b8)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            title="Ranked Bar View"
          >
            <BarChart2 size={13} />
            <span>Bars</span>
          </button>

          <button
            onClick={() => setViewMode("heatmap")}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              fontSize: "0.75rem",
              fontWeight: 600,
              padding: "4px 10px",
              borderRadius: 4,
              background: viewMode === "heatmap" ? "var(--dash-indigo, #6366F1)" : "transparent",
              color: viewMode === "heatmap" ? "#ffffff" : "var(--dash-text-muted, #94a3b8)",
              border: "none",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            title="Heatmap View"
          >
            <Grid size={13} />
            <span>Heatmap</span>
          </button>
        </div>
      </div>

      {/* VIEW CONTENT */}
      {viewMode === "bars" ? (
        /* RANKED BARS VIEW */
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {safeSectors.map((sec, idx) => {
            const secName = sec?.name || `Sector #${idx + 1}`;
            const isSelected = selectedSector === secName;
            const secCount = typeof sec?.count === "number" ? sec.count : 0;
            const secPct = totalAttacks > 0
              ? calculateShare(secCount, totalAttacks)
              : (typeof sec?.percentage === "number" ? sec.percentage : 0);
            const widthPct = Math.max((secCount / maxCount) * 100, 4);

            return (
              <div
                key={secName}
                onClick={() => onSelectSector(isSelected ? null : secName)}
                style={{
                  padding: "12px 14px",
                  borderRadius: 8,
                  background: isSelected
                    ? "rgba(0, 102, 224, 0.15)"
                    : "var(--dash-subcard-bg, rgba(255, 255, 255, 0.02))",
                  border: `1px solid ${
                    isSelected
                      ? "rgba(0, 102, 224, 0.4)"
                      : "var(--dash-card-border, rgba(255, 255, 255, 0.05))"
                  }`,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                }}
                className="rw-sector-row"
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    fontSize: "0.85rem",
                    marginBottom: 8,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: "var(--dash-indigo, #6366F1)" }}>{getSectorIcon(secName)}</span>
                    <span
                      style={{
                        fontWeight: 700,
                        color: "var(--dash-text-primary, #f8fafc)",
                      }}
                    >
                      {secName}
                    </span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontWeight: 800, color: "var(--dash-text-primary, #f8fafc)", fontVariantNumeric: "tabular-nums" }}>
                      {formatNumber(secCount)}
                    </span>

                    <span
                      style={{
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "var(--dash-indigo, #818cf8)",
                        background: "rgba(0, 102, 224, 0.12)",
                        padding: "2px 8px",
                        borderRadius: 4,
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatPercent(secPct)}
                    </span>
                  </div>
                </div>

                {/* Single-hue intensity bar fill */}
                <div
                  style={{
                    width: "100%",
                    height: 5,
                    borderRadius: 3,
                    background: "rgba(255, 255, 255, 0.06)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${widthPct}%`,
                      height: "100%",
                      background: isSelected
                        ? "linear-gradient(90deg, #6366F1 0%, #a855f7 100%)"
                        : "var(--dash-indigo, #6366F1)",
                      borderRadius: 3,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* HEATMAP MATRIX VIEW */
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "150px repeat(auto-fit, minmax(28px, 1fr))",
              gap: 8,
              alignItems: "center",
              fontSize: "0.72rem",
              fontWeight: 700,
              color: "var(--dash-text-muted, #94a3b8)",
              paddingBottom: 6,
              borderBottom: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
            }}
          >
            <span>Sector</span>
            {(Array.isArray(safeSectors[0]?.heatmap) ? safeSectors[0].heatmap : [0, 0, 0, 0]).map((_, idx) => (
              <span key={idx} style={{ textAlign: "center" }}>
                W{idx + 1}
              </span>
            ))}
          </div>

          {safeSectors.map((sec) => {
            const heatmap = Array.isArray(sec?.heatmap) ? sec.heatmap : [];
            const rawMaxVal = heatmap.length > 0 ? Math.max(...heatmap) : 1;
            const maxVal = isFinite(rawMaxVal) && rawMaxVal > 0 ? rawMaxVal : 1;
            return (
              <div
                key={sec.name}
                style={{
                  display: "grid",
                  gridTemplateColumns: "150px repeat(auto-fit, minmax(28px, 1fr))",
                  gap: 8,
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "0.82rem",
                    fontWeight: 700,
                    color: "var(--dash-text-primary, #f8fafc)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={sec.name}
                >
                  {sec.name}
                </div>

                {heatmap.map((cnt, wIdx) => {
                  const intensity = cnt > 0 ? Math.max(0.18, cnt / maxVal) : 0.04;
                  return (
                    <div
                      key={wIdx}
                      title={`${sec.name} (Week ${wIdx + 1}): ${cnt} disclosures`}
                      style={{
                        height: 26,
                        borderRadius: 4,
                        background: cnt > 0 ? `rgba(0, 102, 224, ${intensity})` : "rgba(255, 255, 255, 0.03)",
                        border: "1px solid rgba(255, 255, 255, 0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: cnt > 0 ? "#ffffff" : "var(--dash-text-muted, #64748b)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {cnt > 0 ? cnt : ""}
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* HEATMAP LEGEND */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, marginTop: "auto", paddingTop: 12 }}>
            <span style={{ fontSize: "0.72rem", color: "var(--dash-text-muted, #94a3b8)" }}>Less</span>
            <div style={{ display: "flex", gap: 4 }}>
              {[0.18, 0.45, 0.72, 1.0].map((alpha, idx) => (
                <div
                  key={idx}
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    background: `rgba(0, 102, 224, ${alpha})`,
                  }}
                />
              ))}
            </div>
            <span style={{ fontSize: "0.72rem", color: "var(--dash-text-muted, #94a3b8)" }}>More</span>
          </div>
        </div>
      )}
    </div>
  );
}
